import React from 'react'
import { UsersIcon, StarIcon } from '@heroicons/react/20/solid'
import { useApiQuery } from '../../../utils/api'
import { Link } from 'react-router-dom'
import { Loading, Error } from '../../Elements/Indicators'
import { PageHeader } from '../../Elements/PageHeader'
import { Card } from '../../Elements/Cards'
import { Header4, Pill } from '../../Elements/Type'
import { XProject } from '@jirax/common'

interface ProjectUser {
  id: string
  displayName: string
  avatarUrl: string
  role?: string
}

interface ProjectData extends XProject {
  users: ProjectUser[]
  lead: ProjectUser | null
}

const Projects: React.FC = () => {
  const {
    isLoading,
    error,
    data: projects
  } = useApiQuery<ProjectData[]>(['projects'])

  if (isLoading) {
    return <Loading message={'Loading projects.'} />
  }

  if (error) {
    return <Error message={(error as Error).message} />
  }

  if (!projects) {
    return <Error message={'No projects found.'} />
  }

  projects.sort((a, b) => (a.id > b.id ? -1 : 1))

  return (
    <>
      <PageHeader config={{ pageTitle: 'Projects' }} />
      <main>
        <div className='mx-auto max-w-7xl sm:px-6 lg:px-8'>
          <div className='px-4 pb-8 sm:px-0'>
            {/* GRID OF PROJECT CARDS */}
            <div className='grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3'>
              {projects.map(project => {
                // separate the lead from member list
                const lead = project.lead
                const members = project.users.filter(
                  u => (lead ? u.id !== lead.id : true)
                )
                return (
                  <Link
                    key={project.id}
                    to={'/projects/' + project.key}
                    className='group'
                  >
                    {/* add padding inside the card */}
                    <Card className='h-full p-4'>
                      {/* header */}
                      <div className='flex items-center justify-between'>
                        <Header4 title={project.name} />
                        <Pill text={project.key} color='green' />
                      </div>

                      {/* lead section */}
                      {lead && (
                        <div className='mt-4 flex items-center'>
                          <div className='relative inline-block'>
                            <img
                              className='h-12 w-12 rounded-full object-cover'
                              src={lead.avatarUrl}
                              alt={lead.displayName}
                              title={lead.displayName}
                            />
                            {/* crown / star icon */}
                            <StarIcon
                              className='absolute -bottom-1 -right-1 h-5 w-5 text-yellow-400 drop-shadow'
                              aria-hidden='true'
                            />
                          </div>
                          <div className='ml-3'>
                            <p className='text-sm font-medium text-gray-700'>Project Lead</p>
                            <p className='text-sm text-gray-500'>{lead.displayName}</p>
                          </div>
                        </div>
                      )}

                      {/* members grid */}
                      <div className='mt-4'>
                        <div className='flex flex-wrap items-center gap-2'>
                          {members.map(member => (
                            <img
                              key={member.id}
                              className='h-8 w-8 rounded-full object-cover'
                              src={member.avatarUrl}
                              alt={member.displayName}
                              title={member.displayName}
                            />
                          ))}
                        </div>
                      </div>

                      {/* footer */}
                      <div className='mt-4 flex items-center justify-between text-xs text-gray-500'>
                        <div className='flex items-center'>
                          <UsersIcon className='mr-1.5 h-4 w-4 text-gray-400' />
                          {project.users.length} members
                        </div>
                        <span>ID: {project.id}</span>
                      </div>
                    </Card>
                  </Link>
                )
              })}
            </div>
          </div>
        </div>
      </main>
    </>
  )
}

export default Projects
